import { useState } from 'react';
import type { Member } from '../types';

interface PersonPickerProps {
  /** Human members eligible to be assigned. */
  members: Member[];
  assignedUserId: string | null;
  onAssign: (userId: string) => void;
  /** Omit to hide the "Remove tag" action entirely (e.g. nothing to remove yet). */
  onRemove?: () => void;
  /** Should throw on failure so the input stays populated for a retry. */
  onAddNewPerson: (name: string) => Promise<void>;
  onClose?: () => void;
  /** Small thumbnail shown next to the header text — a face crop or cluster representative. */
  headerCropUrl?: string;
  headerLabel?: string;
}

/**
 * The "who is this?" member-grid + "add new person" picker, extracted out of
 * FaceTagRow (which pairs it with a per-face crop-selector row that doesn't
 * apply everywhere it's needed — e.g. FaceClusterCard names a whole cluster
 * at once, with no "pick which face" step first). This is the reusable atom;
 * FaceTagRow keeps its own multi-face row around one of these.
 */
export function PersonPicker({
  members,
  assignedUserId,
  onAssign,
  onRemove,
  onAddNewPerson,
  onClose,
  headerCropUrl,
  headerLabel = 'Who is this?',
}: PersonPickerProps) {
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  async function handleAddNew() {
    if (!newName.trim() || adding) return;
    setAdding(true);
    try {
      await onAddNewPerson(newName.trim());
      setNewName('');
      onClose?.();
    } catch {
      // leave the input populated so the user can retry
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="bg-slate-50 rounded-xl p-3 border border-indigo-100">
      <div className="flex items-center gap-2 mb-2">
        {headerCropUrl && <img src={headerCropUrl} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" alt="" />}
        <p className="text-xs font-semibold text-slate-700 flex-1">{headerLabel}</p>
        {onClose && (
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">
            ×
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2.5">
        {members.map(m => {
          const isAssigned = assignedUserId === m.user.id;
          return (
            <button
              key={m.user.id}
              type="button"
              onClick={() => onAssign(m.user.id)}
              className={`text-xs px-2.5 py-1.5 rounded-full border font-medium transition-all flex items-center gap-1 ${
                isAssigned
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
              }`}
            >
              {isAssigned && <span>✓</span>}
              {(m.user.name ?? '?').split(' ')[0]}
              {m.relationship && <span className="opacity-60 text-[10px]">· {m.relationship}</span>}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAddNew(); }}
          placeholder="Not here? Add new person…"
          className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
        />
        <button
          type="button"
          onClick={handleAddNew}
          disabled={!newName.trim() || adding}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-indigo-700"
        >
          {adding ? '…' : 'Add'}
        </button>
      </div>
      {assignedUserId && onRemove && (
        <button type="button" onClick={onRemove} className="mt-2 text-xs text-red-500 hover:text-red-700">
          Remove tag
        </button>
      )}
    </div>
  );
}
